import { ColumnType } from '@components/Table/useTable'
import { CellContext } from '@tanstack/react-table'
import { propertyName } from '@utils/utilities'

import { $$ } from '@suntex.core/text'
import { titleCase } from '@suntex.core/text/transforms'

export interface IEntityApiResponse<TItem> {
  items?: TItem[] | undefined
}

export class ColumnBuilder<TItem> {
  public FieldName: string

  public HeaderText?: string

  public Cell?: (item: TItem) => JSX.Element

  constructor(fieldName: string) {
    this.FieldName = fieldName
  }

  public setCell(cell: (item: TItem) => JSX.Element) {
    this.Cell = cell

    return this
  }

  public setHeaderText(headerText: string) {
    this.HeaderText = headerText

    return this
  }
}

export class TableBuilder<TItem> {
  public Columns: ColumnBuilder<TItem>[] = []

  public addColumn(): ColumnBuilder<TItem>
  public addColumn(fieldNameGetter: (item: TItem) => any): ColumnBuilder<TItem>
  public addColumn(fieldName: string): ColumnBuilder<TItem>
  public addColumn(
    field?: string | ((item: TItem) => any)
  ): ColumnBuilder<TItem> {
    let fieldName = ''

    if (typeof field === 'string' || field instanceof String) {
      fieldName = field as string
    }

    if (typeof field === 'function') {
      fieldName = propertyName({} as TItem, field)
    }

    const col = new ColumnBuilder<TItem>(fieldName)

    //default the header text to a cleaned up version of the field name
    col.HeaderText = $$(fieldName, titleCase())
    this.Columns.push(col)
    return col
  }

  public toColumnTypes() {
    return this.Columns.map((x) => {
      const c = {
        id: x.FieldName || Math.random().toString(),
        accessorKey: x.FieldName,
        header: x.HeaderText
      } as ColumnType<TItem>

      //if we have a cell callback
      if (x.Cell) {
        //specify it, currying so that we pass in the row data, which is what our callback expects
        c.cell = (item: CellContext<TItem, unknown>) =>
          x.Cell?.(item.row.original)
      }

      return c
    })
  }
}

interface IVisibleProps {
  isVisible: boolean
  children: any
}

export function Visible(props: IVisibleProps) {
  return props.isVisible ? <>{props.children}</> : <></>
}

interface IMailToProps {
  email: string
  name: string
}

export function MailTo(props: IMailToProps) {
  const name = props.name || props.email

  return props.email ? (
    <a href={`mailto:${props.email}`}>{name}</a>
  ) : (
    <>{name}</>
  )
}
